import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faAward,
  faBookOpen,
  faChartColumn,
  faCircleCheck,
  faCircleInfo,
  faComputerMouse,
  faLightbulb,
  faMagnifyingGlass,
  faMobileScreen,
  faRotateRight,
  faTriangleExclamation,
  faUserCheck,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { apiService } from '../../services/api';
import './AdminDashboard.css';
import './AdminUsageInsights.css';

const number = new Intl.NumberFormat('pt-BR');
const decimal = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });
const safeList = (value) => Array.isArray(value) ? value : [];
const percent = (value) => `${decimal.format(Number(value || 0))}%`;

const dateTime = (value) => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const deviceLabels = {
  mobile: 'Celular',
  desktop: 'Computador',
  tablet: 'Tablet',
  unknown: 'Não identificado',
};

const severityLabels = {
  high: 'Prioridade alta',
  medium: 'Atenção',
  low: 'Oportunidade',
  info: 'Contexto',
};

const Metric = ({ icon, label, value, suffix, detail }) => (
  <article className="admin-metric-card usage-metric">
    <div className="admin-metric-icon"><FontAwesomeIcon icon={icon} /></div>
    <div>
      <span className="admin-metric-label">{label}</span>
      <strong>{typeof value === 'number' ? number.format(value) : value}{suffix}</strong>
      {detail && <small>{detail}</small>}
    </div>
  </article>
);

const InsightCard = ({ item }) => (
  <article className={`usage-insight usage-insight-${item.severity || 'info'}`}>
    <div className="usage-insight-heading">
      <span>{severityLabels[item.severity] || 'Sinal'}</span>
      <strong>{item.title}</strong>
    </div>
    <p>{item.evidence}</p>
    <div><FontAwesomeIcon icon={faLightbulb} /><span>{item.action}</span></div>
  </article>
);

const AdminUsageInsights = () => {
  const [report, setReport] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadReport = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setError('');
      const response = await apiService.admin.usageInsights(days);
      setReport(response.data);
    } catch {
      setError('Não foi possível consolidar o mapa de uso da plataforma.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  useEffect(() => {
    const refresh = () => loadReport({ silent: true });
    const timer = window.setInterval(refresh, 60000);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [loadReport]);

  const traffic = report?.traffic || {};
  const operations = report?.operations || {};
  const opportunities = report?.journey_opportunities || {};
  const freshness = report?.freshness || {};
  const funnel = safeList(report?.funnel);
  const maxFunnel = Math.max(...funnel.map((stage) => Number(stage.count || 0)), 1);
  const devices = safeList(report?.devices);
  const mobile = devices.find((item) => item.device === 'mobile');
  const metrics = [
    { label: 'Sessões reais', value: Number(traffic.sessions || 0), icon: faUsers, detail: `${number.format(traffic.identified_users || 0)} usuários identificados` },
    { label: 'Participação do celular', value: percent(traffic.mobile_share), icon: faMobileScreen, detail: `${number.format(traffic.mobile_sessions || 0)} sessões` },
    { label: 'Páginas por sessão', value: decimal.format(traffic.pages_per_session || 0), icon: faComputerMouse, detail: `${number.format(traffic.page_views || 0)} páginas visualizadas` },
    { label: 'Retorno de usuários', value: percent(traffic.return_rate), icon: faUserCheck, detail: `${number.format(traffic.returning_users || 0)} voltaram em outro dia` },
    { label: 'Inícios de curso', value: Number(operations.course_starts || 0), icon: faBookOpen, detail: 'Ocorridos no período' },
    { label: 'Provas aprovadas', value: Number(operations.passed_exams || 0), icon: faCircleCheck, detail: `${number.format(operations.exam_updates || 0)} resultados atualizados` },
    { label: 'Pagamentos aprovados', value: Number(operations.approved_payments || 0), icon: faAward, detail: `${number.format(operations.pending_payments || 0)} pendentes` },
    { label: 'Buscas sem resultado', value: percent(traffic.zero_result_rate), icon: faMagnifyingGlass, detail: `${number.format(traffic.zero_result_searches || 0)} de ${number.format(traffic.searches || 0)} buscas` },
    { label: 'Prontos para a prova', value: Number(opportunities.ready_for_exam_users || 0), icon: faCircleCheck, detail: `${number.format(opportunities.ready_for_exam_courses || 0)} curso(s) sem aprovação` },
    { label: 'Reprovaram na prova', value: Number(opportunities.failed_exam_users || 0), icon: faTriangleExclamation, detail: 'Alunos que podem tentar novamente' },
    { label: 'Parados há 7 dias', value: Number(opportunities.partial_stalled_users || 0), icon: faBookOpen, detail: 'Com progresso parcial no curso' },
  ];

  return (
    <main className="admin-dashboard-page">
      <div className="admin-dashboard-shell">
        <header className="admin-dashboard-header usage-header">
          <div>
            <span className="admin-eyebrow">Mapa de percepção de uso</span>
            <h1>Jornada e oportunidades</h1>
            <p>Uma leitura consolidada do cadastro ao certificado, com comparação entre celular e computador e prioridades de melhoria.</p>
          </div>
          <div className="admin-header-actions">
            <Link className="admin-nav-button" to="/admin/analytics" title="Abrir comportamento detalhado">
              <FontAwesomeIcon icon={faChartColumn} />
              Comportamento
            </Link>
            <Link className="admin-nav-button" to="/admin" title="Voltar ao painel geral">
              <FontAwesomeIcon icon={faArrowLeft} />
              Painel geral
            </Link>
            <button className="admin-icon-button" onClick={() => loadReport()} type="button" title="Atualizar mapa">
              <FontAwesomeIcon icon={faRotateRight} spin={loading} />
            </button>
          </div>
        </header>

        <div className="usage-toolbar" aria-label="Período analisado">
          <div>
            <span>Período analisado</span>
            <div className="usage-period-control">
              {[7, 30, 90, 180].map((period) => (
                <button type="button" key={period} className={days === period ? 'active' : ''} onClick={() => setDays(period)}>
                  {period} dias
                </button>
              ))}
            </div>
          </div>
          <div className={`usage-confidence usage-confidence-${report?.confidence?.level || 'low'}`}>
            <FontAwesomeIcon icon={faCircleInfo} />
            <span><strong>{report?.confidence?.label || 'Calculando base'}</strong>{report?.confidence?.description}</span>
          </div>
          <div className={`usage-live-status usage-live-status-${freshness.status || 'stale'}`}>
            <i aria-hidden="true" />
            <span>
              <strong>{freshness.status === 'live' ? 'Dados ao vivo' : 'Dados sem atividade recente'}</strong>
              {number.format(freshness.events_last_hour || 0)} eventos em {number.format(freshness.sessions_last_hour || 0)} sessões na última hora
            </span>
          </div>
          <small>Último evento {dateTime(freshness.latest_event_at)} · painel {dateTime(report?.generated_at)}</small>
        </div>

        {error && (
          <div className="admin-alert">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            <span>{error}</span>
          </div>
        )}

        {loading && !report ? (
          <div className="admin-loading">
            <FontAwesomeIcon icon={faRotateRight} spin />
            <span>Consolidando a jornada dos usuários...</span>
          </div>
        ) : (
          <>
            <section className="admin-metrics-grid usage-metrics-grid" aria-label="Resumo de uso">
              {metrics.map((item) => <Metric key={item.label} {...item} />)}
            </section>

            <section className="usage-priorities" aria-labelledby="usage-priorities-title">
              <div className="usage-section-heading">
                <div><span>Leitura automática</span><h2 id="usage-priorities-title">O que merece atenção agora</h2></div>
                <small>Regras baseadas em diferenças relevantes e volume mínimo</small>
              </div>
              <div className="usage-insight-grid">
                {safeList(report?.insights).map((item, index) => <InsightCard item={item} key={`${item.area}-${index}`} />)}
              </div>
            </section>

            <div className="usage-primary-grid">
              <section className="admin-section usage-funnel-section">
                <div className="admin-section-title">
                  <div><h2>Funil da jornada</h2><small>Coorte de cadastros feitos nos últimos {days} dias</small></div>
                  <span>{number.format(funnel[0]?.count || 0)} cadastros válidos</span>
                </div>
                <div className="usage-funnel">
                  {funnel.map((stage, index) => (
                    <div className="usage-funnel-row" key={stage.key}>
                      <div className="usage-funnel-label">
                        <span>{index + 1}</span>
                        <div><strong>{stage.label}</strong><small>{percent(stage.rate_from_start)} dos cadastrados</small></div>
                      </div>
                      <div className="usage-funnel-track"><i style={{ width: `${(stage.count / maxFunnel) * 100}%` }} /></div>
                      <div className="usage-funnel-value">
                        <strong>{number.format(stage.count)}</strong>
                        {index > 0 && <small>{percent(stage.rate_from_previous)} avançaram · {number.format(stage.drop_from_previous)} pararam</small>}
                      </div>
                    </div>
                  ))}
                  {funnel.length === 0 && <div className="admin-empty">Ainda não há cadastros válidos neste período.</div>}
                </div>
              </section>

              <section className="admin-section usage-mobile-summary">
                <div className="admin-section-title"><h2>Leitura do celular</h2><span>{percent(traffic.mobile_share)} das sessões</span></div>
                {mobile ? (
                  <div className="usage-mobile-stats">
                    <div><span>Interesse em cursos</span><strong>{percent(mobile.course_interest_rate)}</strong><small>{number.format(mobile.course_interest_sessions)} sessões clicaram em curso</small></div>
                    <div><span>Chegaram aos módulos</span><strong>{percent(mobile.module_reach_rate)}</strong><small>{number.format(mobile.module_sessions)} sessões</small></div>
                    <div><span>Chegaram à prova</span><strong>{percent(mobile.exam_reach_rate)}</strong><small>{number.format(mobile.exam_sessions)} sessões</small></div>
                    <div><span>Chegaram ao certificado</span><strong>{percent(mobile.certificate_reach_rate)}</strong><small>{number.format(mobile.certificate_sessions)} sessões</small></div>
                  </div>
                ) : <div className="admin-empty">Nenhuma sessão de celular registrada no período.</div>}
              </section>
            </div>

            <section className="admin-section usage-wide-section">
              <div className="admin-section-title">
                <div><h2>Celular x computador</h2><small>Alcance de páginas dentro de cada tipo de sessão</small></div>
                <span>Usuários com vários dispositivos podem aparecer em mais de uma linha</span>
              </div>
              <div className="usage-table-wrap">
                <table className="usage-table">
                  <thead><tr><th>Dispositivo</th><th>Sessões</th><th>Páginas/sessão</th><th>Clique em curso</th><th>Módulos</th><th>Prova</th><th>Certificado</th></tr></thead>
                  <tbody>
                    {devices.map((item) => (
                      <tr key={item.device} className={item.device === 'mobile' ? 'usage-mobile-row' : ''}>
                        <td><strong>{deviceLabels[item.device] || item.device}</strong><small>{number.format(item.identified_users)} usuários identificados</small></td>
                        <td>{number.format(item.sessions)}</td>
                        <td>{decimal.format(item.pages_per_session)}</td>
                        <td>{percent(item.course_interest_rate)}<small>{number.format(item.course_interest_sessions)} sessões</small></td>
                        <td>{percent(item.module_reach_rate)}<small>{number.format(item.module_sessions)} sessões</small></td>
                        <td>{percent(item.exam_reach_rate)}<small>{number.format(item.exam_sessions)} sessões</small></td>
                        <td>{percent(item.certificate_reach_rate)}<small>{number.format(item.certificate_sessions)} sessões</small></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {devices.length === 0 && <div className="admin-empty">Sem sessões registradas neste período.</div>}
              </div>
            </section>

            <section className="admin-section usage-wide-section">
              <div className="admin-section-title">
                <div><h2>Onde cada curso perde usuários</h2><small>Cadastros em curso feitos no período, acompanhados nas etapas posteriores</small></div>
                <span>Ordenado pela maior perda até a aprovação</span>
              </div>
              <div className="usage-table-wrap">
                <table className="usage-table usage-course-table">
                  <thead><tr><th>Curso</th><th>Iniciaram</th><th>Acessaram módulo</th><th>Concluíram estudo</th><th>Prova</th><th>Reprovaram</th><th>Aprovaram</th><th>Compraram</th><th>Maior perda</th></tr></thead>
                  <tbody>
                    {safeList(report?.course_bottlenecks).map((course) => (
                      <tr key={course.course_id}>
                        <td><strong>{course.course_title}</strong><small>{percent(course.completion_rate)} concluíram · {percent(course.purchase_rate)} compraram após aprovação</small></td>
                        <td>{number.format(course.started)}</td>
                        <td>{number.format(course.reached_module)}</td>
                        <td>{number.format(course.study_completed)}</td>
                        <td>{number.format(course.reached_exam)}</td>
                        <td>{number.format(course.failed)}</td>
                        <td>{number.format(course.passed)}</td>
                        <td>{number.format(course.paid)}</td>
                        <td><span className="usage-gap-pill">{number.format(course.main_gap?.count || 0)} · {course.main_gap?.label || '-'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {safeList(report?.course_bottlenecks).length === 0 && <div className="admin-empty">Nenhum curso iniciado no período.</div>}
              </div>
            </section>

            <div className="usage-signal-grid">
              <section className="admin-section">
                <div className="admin-section-title"><h2>Buscas sem resposta</h2><span>Demanda não atendida</span></div>
                <div className="usage-signal-list">
                  {safeList(report?.search_gaps).map((item) => (
                    <div key={item.query}><span><strong>{item.query}</strong><small>{number.format(item.searches)} busca(s)</small></span><b>{percent(item.zero_result_rate)}</b></div>
                  ))}
                  {safeList(report?.search_gaps).length === 0 && <div className="admin-empty">Nenhuma busca sem resultado.</div>}
                </div>
              </section>

              <section className="admin-section">
                <div className="admin-section-title"><h2>Cliques repetidos</h2><span>Possível hesitação</span></div>
                <div className="usage-signal-list">
                  {safeList(report?.repeated_interactions).map((item, index) => (
                    <div key={`${item.page_path}-${item.element_label}-${index}`}><span><strong>{item.element_label}</strong><small>{item.page_path} · {deviceLabels[item.device] || item.device}</small></span><b>{number.format(item.affected_sessions)} sessões</b></div>
                  ))}
                  {safeList(report?.repeated_interactions).length === 0 && <div className="admin-empty">Nenhum padrão repetido relevante.</div>}
                </div>
              </section>

              <section className="admin-section">
                <div className="admin-section-title"><h2>Últimas páginas</h2><span>Indício de saída</span></div>
                <div className="usage-signal-list">
                  {safeList(report?.last_pages).map((item) => (
                    <div key={item.page_path}><span><strong>{item.page_path}</strong><small>Último evento da sessão</small></span><b>{number.format(item.sessions)}</b></div>
                  ))}
                  {safeList(report?.last_pages).length === 0 && <div className="admin-empty">Sem páginas registradas.</div>}
                </div>
              </section>
            </div>

            <details className="usage-methodology">
              <summary>Como interpretar este mapa</summary>
              <p>{report?.methodology?.cohort}</p>
              <p>{report?.methodology?.traffic}</p>
              <ul>{safeList(report?.methodology?.limitations).map((item) => <li key={item}>{item}</li>)}</ul>
            </details>
          </>
        )}
      </div>
    </main>
  );
};

export default AdminUsageInsights;
